import { Component, OnDestroy, OnInit } from '@angular/core';
import { DwComponent, DwComponentType } from '@devwareapps/devware-cap';
import { Router } from '@angular/router';
import { AppMetaDataItemNames, QuizEntity, QuizResultStatusAllItems } from '../../../../meta-data/app-meta-data.service';
import { DailyQuizSetupResult, DailyQuizStatusResult } from '../../models/daily-quiz-setup.model';
import { QuizCompleteResult } from '../../models/quiz-complete-result.model';
import { QuizSetupResult } from '../../models/quiz-setup-result.model';
import { QuizViewAction, QuizViewActionType } from '../../models/quiz-view-action.enum';
import { QuizRepositoryService } from '../../services/quiz-repository.service';

@DwComponent({
  componentType: DwComponentType.pageComponent,
  key: 'daily-quiz-page',
  name: 'Daily Quiz Page',
  isGlobal: true
})
@Component({
  selector: 'app-daily-quiz-page',
  templateUrl: './daily-quiz-page.component.html',
  styleUrls: ['./daily-quiz-page.component.scss']
})
export class DailyQuizPageComponent implements OnInit, OnDestroy {
  dailyQuizSetup: DailyQuizSetupResult;
  quiz: QuizEntity;
  quizSetup: QuizSetupResult;
  isLoading = true;
  isGrading = false;
  reviewAnswers = false;
  errorMessage: string;
  private gradePollTimer: any;
  private gradePollCount = 0;
  private readonly maxGradePollCount = 60;
  readonly skeletonSteps = [1, 2, 3, 4, 5];
  readonly skeletonAnswers = [1, 2, 3, 4];

  constructor(
    private quizRepositoryService: QuizRepositoryService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.loadInitialState();
  }

  ngOnDestroy(): void {
    if (this.gradePollTimer) {
      clearTimeout(this.gradePollTimer);
    }
  }

  quizComplete(result: QuizCompleteResult): void {
    if (result.isCancelled) {
      this.router.navigate(['/']);
      return;
    }

    this.isGrading = true;
    this.gradePollCount = 0;
    this.dailyQuizSetup = null;
    this.quizSetup = null;

    // Quiz grading runs in the save interceptor, so a terminal save response can
    // be reloaded immediately. Keep polling only as a fallback for an ungraded response.
    if (this.isGraded(result.quizResult)) {
      this.loadDailyQuiz(false);
      return;
    }

    this.scheduleGradePoll();
  }

  quizSubmissionStarted(): void {
    if (this.gradePollTimer) {
      clearTimeout(this.gradePollTimer);
      this.gradePollTimer = null;
    }

    this.isGrading = true;
    this.isLoading = false;
    this.errorMessage = null;
    this.gradePollCount = 0;
  }

  quizSubmissionFailed(): void {
    this.isGrading = false;
    this.loadDailyQuiz(false);
  }

  quizResultAction(action: QuizViewAction): void {
    if (action.actionType === QuizViewActionType.viewAnswers) {
      this.reviewAnswers = true;
      return;
    }

    if (action.actionType === QuizViewActionType.retake && this.dailyQuizSetup.CanStartAnotherAttempt) {
      this.loadDailyQuiz(true);
    }
  }

  closeReview(): void {
    this.reviewAnswers = false;
  }

  private loadDailyQuiz(startNewAttempt: boolean): void {
    this.isLoading = !this.isGrading;
    this.errorMessage = null;
    this.reviewAnswers = false;

    this.quizRepositoryService.getDailyQuiz({ StartNewAttempt: startNewAttempt })
      .subscribe({
        next: setup => {
          if (setup?.IsSuccess && !setup.QuizResult && !startNewAttempt) {
            this.loadDailyQuiz(true);
            return;
          }

          if (!setup?.IsSuccess || !setup.QuizResult) {
            this.errorMessage = setup?.ErrorMessage || 'Today\'s Daily Flight Check is not available.';
            this.isLoading = false;
            this.isGrading = false;
            return;
          }

          if (this.isGrading && !this.isGraded(setup.QuizResult)) {
            this.continueGradePolling();
            return;
          }

          this.dailyQuizSetup = setup;
          this.buildQuizViewModel(setup);
          this.isLoading = false;
          this.isGrading = false;
        },
        error: () => {
          this.errorMessage = 'Today\'s Daily Flight Check could not be loaded.';
          this.isLoading = false;
          this.isGrading = false;
        }
      });
  }

  private loadInitialState(): void {
    this.isLoading = true;
    this.quizRepositoryService.getDailyQuizStatus()
      .subscribe({
        next: status => {
          if (!status?.IsSuccess || !status.IsAvailable) {
            this.errorMessage = status?.ErrorMessage || 'Today\'s Daily Flight Check is not available.';
            this.isLoading = false;
            return;
          }

          this.loadDailyQuiz(!status.QuizResult);
        },
        error: () => {
          this.errorMessage = 'Today\'s Daily Flight Check could not be loaded.';
          this.isLoading = false;
        }
      });
  }

  private scheduleGradePoll(): void {
    this.gradePollTimer = setTimeout(() => this.pollForGrade(), 750);
  }

  private pollForGrade(): void {
    this.quizRepositoryService.getDailyQuizStatus()
      .subscribe({
        next: status => this.handleGradeStatus(status),
        error: () => this.continueGradePolling()
      });
  }

  private handleGradeStatus(status: DailyQuizStatusResult): void {
    if (this.isGraded(status?.QuizResult)) {
      this.loadDailyQuiz(false);
      return;
    }

    this.continueGradePolling();
  }

  private continueGradePolling(): void {
    this.gradePollCount++;
    if (this.gradePollCount < this.maxGradePollCount) {
      this.scheduleGradePoll();
      return;
    }

    this.isGrading = false;
    this.errorMessage = 'Your Daily Flight Check is still being graded. Please return to the homepage and open the result again.';
  }

  private isGraded(quizResult: { QuizResultStatusId?: number }): boolean {
    return quizResult?.QuizResultStatusId === QuizResultStatusAllItems.Passed
      || quizResult?.QuizResultStatusId === QuizResultStatusAllItems.Failed;
  }

  private buildQuizViewModel(setup: DailyQuizSetupResult): void {
    this.quiz = {
      _itemName: AppMetaDataItemNames.Quiz,
      QuizName: setup.CourseName ? `Daily Flight Check: ${setup.CourseName}` : 'Daily Flight Check',
      QuizTypeIdDisplay: 'Daily Quiz',
      MaxQuestionsToShow: setup.QuestionCount,
      QuizQuestionLink: setup.QuestionLinks
    };

    this.quizSetup = {
      IsSuccess: true,
      Quiz: this.quiz,
      QuizResult: setup.QuizResult,
      IsTestMode: false,
      AllQuizQuestionLinks: setup.QuestionLinks,
      CurrentQuizQuestionLinkIds: setup.QuestionLinks.map(link => link.QuizQuestionLinkId)
    };
  }
}
